import type { ReactNode } from 'react';
import { LogoLockup } from '@/components/Logo';

interface AuthSplitLayoutProps {
  /** Large headline on the brand panel, e.g. "Every shop, one console.". */
  headline: ReactNode;
  /** One or two sentences under the headline. */
  blurb: ReactNode;
  /**
   * Selling points, one line each. Kept to a single line apiece because they are
   * scanned, not read; the panel scrolls rather than clips if the list outgrows
   * a short viewport, so length is a legibility decision, not a layout limit.
   */
  points: string[];
  /** Heading above the form. Says what this sign-in is FOR. */
  title: string;
  /** Supporting line under the form heading. */
  subtitle: string;
  /** The form itself, plus any footer links. */
  children: ReactNode;
}

/**
 * The two-panel sign-in shell shared by /admin/login and /portal/login.
 *
 * Layout: brand panel left (55%), form right (45%). The panel is `bg-panel`,
 * black, and carries the pitch — headline, blurb, tick list. Only this panel
 * and the primary buttons are dark; the console's interior stays light.
 *
 * The lockup lives above the sign-in fields on the WHITE side, in its ink
 * colouring, at every width — not on the dark panel. So the brand mark is
 * present whether or not the panel is showing, which matters because on screens
 * narrower than `lg` the panel is dropped entirely rather than stacked: a tall
 * marketing block above a login form just pushes the fields off a phone screen.
 *
 * Both logins share this shell deliberately: they are the same product, and the
 * only thing that should differ is the copy.
 */
export function AuthSplitLayout({
  headline,
  blurb,
  points,
  title,
  subtitle,
  children,
}: AuthSplitLayoutProps) {
  return (
    <div className="flex min-h-screen bg-white">
      {/* Brand panel. aria-hidden: it is promotional copy, and every fact a
          screen-reader user needs to sign in is in the form panel. */}
      <section
        aria-hidden="true"
        // 55/45 against the form panel — the form side takes the rest via
        // `flex-1`, so this percentage is the single source of truth for the
        // split. No max-width cap: one would silently win over the percentage
        // on wide screens and drift the split back toward 50/50, which is what
        // it used to do.

        className="relative hidden w-[55%] shrink-0 select-none overflow-hidden bg-panel lg:flex lg:flex-col"
        style={{
          // An even wash across the whole panel rather than a light spot on it.
          // This used to be a 70rem radial "glow" parked off the top-right
          // corner at 0.55 opacity — invisible while the panel was black, but
          // against the navy it read as a blotch, as though the fill had failed
          // rather than as lighting. A single linear ramp corner-to-corner has
          // nowhere to end abruptly, so the panel reads as one colour with
          // depth. Steps are the panel token's own light/DEFAULT/dark.
          backgroundImage:
            'linear-gradient(155deg, #1F1F23 0%, #111111 48%, #050506 100%)',
        }}
      >
        <div className="pointer-events-none absolute -left-32 bottom-[-18rem] h-[46rem] w-[46rem] rounded-full border border-white/[0.07]" />
        <div className="pointer-events-none absolute -left-16 bottom-[-14rem] h-[34rem] w-[34rem] rounded-full border border-white/[0.05]" />

        {/* Centred, but it must never clip. At this type size the block needs
            ~680px of height, and a 1366x768 laptop only gives the viewport about
            660 — with the panel's overflow-hidden that would silently cut the
            logo off. So: generous padding only when there is vertical room (a
            HEIGHT query — `xl:` is width and 1366x768 would wrongly qualify),
            and the column scrolls as a last resort instead of clipping.
            min-h-full on the inner box keeps it centred while still being able
            to grow past the viewport. */}
        <div className="relative flex-1 overflow-y-auto">
          <div className="flex min-h-full flex-col items-center justify-center px-12 py-12 text-center xl:px-16 [@media(min-height:860px)]:py-20">
            {/* No lockup here. The brand mark lives above the sign-in form on
                the white side instead, in its ink colouring — this panel now
                carries the pitch only. That is also why nothing on this panel
                needs to avoid pure white any more. */}
            <div className="max-w-2xl">
              {/* White, by explicit request. Worth knowing if this is revisited:
                  it was tinted (slate-300, then panel-tint blue) precisely
                  because a white headline at 48px bold is the same colour as
                  the wordmark but far more of it, so the two competed and the
                  logo lost. What makes it work now is that the lockup is
                  right-aligned while this stays centred — they are separated by
                  POSITION instead of colour. Re-centre the logo and the clash
                  comes back; use `text-panel-tint` again if that happens. */}
              <h2 className="text-[2.5rem] font-bold leading-[1.12] tracking-tight text-white xl:text-[3rem]">
                {headline}
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-[17px] leading-relaxed text-slate-400 xl:text-lg">
                {blurb}
              </p>

              {/* Centred as a block, but the items themselves stay left-aligned:
                  centring each line individually would stagger the ticks and the
                  list would read as ragged rather than deliberate. */}
              <ul className="mt-10 inline-flex flex-col gap-3 text-left">
                {points.map((point) => (
                  <li
                    key={point}
                    // 1.2rem = 19.2px, a straight 20% up from the text-base this
                    // was. An arbitrary value rather than text-lg (18px, only
                    // 12.5%) or text-xl (20px, 25%) — neither Tailwind step
                    // lands on the size that was actually asked for.
                    className="flex items-start gap-3 text-[1.2rem] text-slate-400"
                  >
                    {/* The one chromatic accent on the panel, echoing the red in
                        the lockup directly above. Safe here specifically because
                        this panel is decorative marketing with no controls in it
                        — the "red means destructive" rule governs buttons and
                        validation, and there are none on this side of the split.
                        It also stops the ticks competing with the logo, which is
                        what a white tick was doing. */}
                    <svg
                      viewBox="0 0 20 20"
                      className="mt-0.5 h-6 w-6 shrink-0 text-mark"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.25"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 10.5l4 4 8-9" />
                    </svg>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* A flex sibling of the scroll area rather than absolutely positioned,
            so it can never end up sitting on top of the centred block. */}
        <p className="relative shrink-0 px-12 pb-10 text-center text-xs text-white/40">
          © {new Date().getFullYear()} SelloKart. All rights reserved.
        </p>
      </section>

      {/* Form panel */}
      <main className="flex flex-1 items-center justify-center px-5 py-12 sm:px-8">
        {/* Sits slightly above true centre — optically centred rather than
            mathematically so, which is what a signed-in-from block usually
            wants. Gated behind a HEIGHT query: on a short viewport the block is
            already close to filling the screen, and shifting it up there would
            push the logo under the top edge, since `items-center` has no
            scrollable overflow above it to give the content back. */}
        <div className="w-full max-w-sm [@media(min-height:700px)]:-translate-y-[10%]">
          {/* The brand mark, sitting directly above the sign-in fields. Shown at
              EVERY width, not just below `lg` as it once was — this is now the
              only place the lockup appears, so hiding it on desktop would leave
              the wide layout with no logo at all.

              Ink, not the reversed white artwork: this side of the split is
              white, and the reversed variant would be invisible on it.

              Sized inside the form's own max-w-sm (384px), which is the ceiling
              here — the lockup is ~7:1, so width is what drives its height.
              Desktop is at that ceiling now, which lines the lockup up exactly
              with the input fields below it. Phones stay smaller: the same
              width there eats the top of a short screen and pushes the fields
              under the fold. */}
          <div className="mb-8">
            <LogoLockup className="w-[17rem] lg:w-96" />
            {/* The artwork is decorative to assistive tech, and dropping the
                old text would have taken the product name with it. */}
            <span className="sr-only">SelloKart Cloud</span>
          </div>

          {/* Visually removed at every width — the lockup above opens the page
              now, and the heading was repeating what the subtitle already says.

              `sr-only`, NOT deleted: this is the page's only h1, and a sign-in
              page with no heading at all leaves screen-reader users without a
              landmark to navigate to. It costs nothing visually and keeps the
              document outline intact. `title` is still a required prop and is
              still what distinguishes the two logins to assistive tech. */}
          <h1 className="sr-only">{title}</h1>
          <p className="mt-2 text-sm text-slate-500">{subtitle}</p>

          <div className="mt-8">{children}</div>
        </div>
      </main>
    </div>
  );
}
